import { useEffect } from "react";
import useSocket from "../LivePage/hooks/useSocket/useSocket";
import { useStore } from "../store/store";
import { ILiveDetails } from "../SignupPage/hooks/useGetLiveDetails";
import { useLocation } from "react-router-dom";
import useSetParticipated from "./useSetParticipated";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { enUS, fr } from "date-fns/locale";
import { getStoredListenerName } from "../LivePage/utils/listenerNameStorage";

const useLiveDetailsUpdate = (data: ILiveDetails | undefined, subscriberFullNameFromApi?: string) => {
	const { i18n, t } = useTranslation();
	const setLive = useStore((state) => state.setLive);
	const setDescription = useStore((state) => state.setDescription);
	const setLiveActive = useStore((state) => state.setLiveActive);
	const setStatus = useStore((state) => state.setStatus);
	const setCanReplyToQuestions = useStore((state) => state.setCanReplyToQuestions);
	const setWithSubscription = useStore((state) => state.setWithSubscription);
	const setCanComment = useStore((state) => state.setCanComment);
	const setChat = useStore((state) => state.setChat);
	const setVideoEmbedUrl = useStore((state) => state.setVideoEmbedUrl);
	const setLiveId = useStore((state) => state.setLiveId);
	const setListenerFullName = useStore((state) => state.setListenerFullName);
	const location = useLocation();
	const queryParams = new URLSearchParams(location.search);
	const token = queryParams.get("token");
	const setParticipated = useSetParticipated(token);
	const setVideoBeforeLive = useStore((state) => state.setVideoBeforeLive);
	const setLiveTitle = useStore((state) => state.setLiveTitle);
	const setSkinInfos = useStore((state) => state.setSkinInfos);
	const setIsIntranet = useStore((state) => state.setIsIntranet);
	const setStartDate = useStore((state) => state.setStartDate);
	const setEndDate = useStore((state) => state.setEndDate);
	const setCanCommentBeforeLive = useStore((state) => state.setCanCommentBeforeLive);
	const setBackstage = useStore((state) => state.setBackstage);
	const user = useStore((state) => state.user);

	useEffect(() => {
		if (token && data?.active === 1 && data?.status === 0 && user?.role !== "moderator") {
			setParticipated.mutate();
		}
    // eslint-disable-next-line react-hooks/exhaustive-deps
	}, [token, data?.active, data?.status, user]);

	useEffect(() => {
		if (data) {
			document.title = data.title;
			setLive(data);
			setDescription(data?.description);
			setLiveActive(data.active);
			setStatus(data.status);
			setCanReplyToQuestions(data.canReplyToComments);
			setWithSubscription(data.subscription);
			setCanComment(data.canComment);
			setChat(data.chat);
			setVideoEmbedUrl(data.videoEmbedUrl);
			setLiveId(data.id);
			setVideoBeforeLive(data.conf?.video?.url || "");
			setIsIntranet(data.isIntranet);
			setLiveTitle(data.title);
			const start = new Date(data.date.StartDate?.replace(/\+\d{2}:\d{2}/, "Z"));
			const end = new Date(data.date.EndDate?.replace(/\+\d{2}:\d{2}/, "Z"));
			
			setStartDate(
			  format(start, "eeee d MMMM yyyy", {
				locale: i18n.language === "en" ? enUS : fr,
			  })
			);
			
			setEndDate(
			  `${t("signup_page.from")} ${format(start, "HH'h'mm")} ${t("signup_page.to")} ${format(end, "HH'h'mm")}`
			);
			setCanCommentBeforeLive(data.canCommentBeforeLive);
			setBackstage(data.private_chat);
			setSkinInfos(data.liveSkin);

			// Name for chat / Q&A: profile → API hint (subscriber) → localStorage (per live id)
			if (user?.fullName) {
				setListenerFullName(user.fullName);
			} else {
				const combined = [user?.name || "", user?.lastName || ""].filter(Boolean).join(" ");
				if (combined) {
					setListenerFullName(combined);
				} else if (subscriberFullNameFromApi?.trim()) {
					setListenerFullName(subscriberFullNameFromApi.trim());
				} else {
					const saved = getStoredListenerName(data.id);
					setListenerFullName(saved);
				}
			}
		}
    // eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, user, subscriberFullNameFromApi, setListenerFullName, i18n.language, t]);
	useSocket(data?.id);
};

export default useLiveDetailsUpdate;
